import type { CookieConfig, CookieServices } from 'services/analytics/CookieConfig';

export const MARKETING_INFO_COOKIE_DURATION_DAYS = 90;
export const MAX_COOKIE_LENGTH = 128;

const isCloverDomain: boolean = window.location.host.indexOf('clover.com') !== -1;

document.addEventListener('DOMContentLoaded', removeNonAsciiCookies);

function removeNonAsciiCookies() {
  const ASCII_ONLY = /^[\u0000-\u007F]*$/;

  document.cookie
    .split(';')
    .filter(cookie => !ASCII_ONLY.test(cookie))
    .forEach(cookie => {
      const [name] = cookie.split('=');
      eraseCookie(name);
    });
}

export function setCookie(
  name: string,
  value: string | string[],
  days?: number,
  includeSubdomains = true,
  ignoreTruncation = false,
) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }

  if (!ignoreTruncation) {
    value = truncateCookie(name, value);
  }

  document.cookie =
    `${name}=${value || ''}` +
    `${includeSubdomains && isCloverDomain ? '; domain=.clover.com' : ''}` +
    `${expires}; path=/`;
}

function truncateCookie(name: string, value: string | string[]) {
  if (typeof value === 'string') {
    value = truncateCookieLargerThanMaximum(name, value);
  }

  if (Array.isArray(value)) {
    value = value.map(t => {
      return truncateCookieLargerThanMaximum(name, t);
    });
  }

  return value;
}

function truncateCookieLargerThanMaximum(name: string, value: string) {
  if (value.length > MAX_COOKIE_LENGTH) {
    return value.substring(0, MAX_COOKIE_LENGTH);
  }
  return value;
}

export function setCookieIfValueExists(
  cookieName: string,
  value?: string,
  days = MARKETING_INFO_COOKIE_DURATION_DAYS,
) {
  if (value) {
    setCookie(cookieName, value, days);
  }
}

export function readCookie(name: string) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  if (match) return match[2];
  return null;
}

export function readCookies(arr: string[]): { [x: string]: string } {
  return arr.reduce((acc, property) => {
    const value = readCookie(property);
    if (!value) return acc;
    acc[property] = value;
    return acc;
  }, {});
}

export function readCookiesForService(
  cookieConfig: CookieConfig[],
  service: keyof CookieServices,
): { [x: string]: string } {
  return cookieConfig.reduce((acc, config) => {
    const isCookieSentToService = config && config.to && config.to[service];
    if (!isCookieSentToService) return acc;

    const serviceOptions = config.to[service]!;
    const needsSpecialName = !!serviceOptions.as;

    const cookieName = needsSpecialName ? serviceOptions.as : config.cookieName;
    if (cookieName === undefined) return acc;

    const value = readCookie(config.cookieName);
    if (!value) return acc;
    acc[cookieName] = value;
    return acc;
  }, {});
}

export function eraseCookie(name: string, includeSubdomains?: boolean) {
  setCookie(name, '', -1, includeSubdomains);
}

export function eraseCookiesDirectly(name: string, includeSubdomains = true) {
  if (includeSubdomains) {
    // Remove cookie for all subdomains
    const hostname = window.location.hostname.split('.');
    while (hostname.length > 1) {
      const domain = '.' + hostname.join('.');
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=' + domain + '; path=/';
      hostname.shift();
    }
  } else {
    // Remove cookie for current domain
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  }
}
